/**
 * Componente: Captura de foto por cámara (getUserMedia)
 * Se embebe inline dentro del formulario de contribuyente cuando el
 * municipio tiene metodo_captura_foto="camara" (o cuando el operador elige
 * esa opción, si todavía no hay un método configurado). Vista previa en
 * vivo, botón de captura, y "repetir toma" antes de confirmar. Al tocar
 * "Usar esta foto" no se sube todavía: se abre el mismo editor de recorte
 * que usa el flujo de archivo (RecorteFotoModal) sobre la toma capturada, y
 * recién al confirmar el recorte se llama a onCapturar con el blob JPG ya
 * recortado — "Repetir toma" sigue disponible como antes, tanto desde la
 * vista previa como cancelando el recorte.
 */

import React, { useEffect, useRef, useState } from 'react';
import { STROKE_DEGRADE, IconCamara, IconRenovar, IconExito, IconAdvertencia } from './icons';
import RecorteFotoModal from './RecorteFotoModal';

const CapturaFotoCamara = ({ onCapturar, subiendo }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState('');
  const [listaParaCapturar, setListaParaCapturar] = useState(false);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [mostrarRecorte, setMostrarRecorte] = useState(false);

  useEffect(() => {
    let cancelado = false;

    const iniciarCamara = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError('Este navegador no soporta acceso a la cámara. Probá con Chrome o Firefox actualizados, o hablá con el administrador para cambiar el método de carga de foto a "Subir archivo".');
        return;
      }
      try {
        // 'ideal' (no 'exact'): en celular pide la cámara trasera — mejor
        // para fotografiar a otra persona que la frontal — pero si el
        // dispositivo no tiene trasera (o es una notebook con una sola
        // cámara) cae a la que haya disponible en vez de fallar.
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });
        if (cancelado) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => setListaParaCapturar(true);
        }
      } catch (err) {
        console.error('Error al acceder a la cámara:', err);
        setError('No se pudo acceder a la cámara. Puede ser que el navegador no tenga permiso, o que no haya ninguna cámara disponible en este equipo. Si el problema persiste, hablá con el administrador del municipio para cambiar el método de carga de foto a "Subir archivo".');
      }
    };

    iniciarCamara();

    return () => {
      cancelado = true;
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const capturar = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    setFotoPreview(canvas.toDataURL('image/jpeg', 0.9));
  };

  const repetirToma = () => {
    setFotoPreview(null);
    setMostrarRecorte(false);
  };

  // Ya no sube directo: abre el editor de recorte sobre la toma ya
  // capturada (fotoPreview, un dataURL). onCapturar(blob) se llama recién
  // cuando se confirma el recorte, con el blob ya recortado.
  const alConfirmarRecorte = (blob) => {
    setMostrarRecorte(false);
    onCapturar(blob);
  };

  if (error) {
    return (
      <div className="alert alert-error">
        <IconAdvertencia size={18} />
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="captura-camara">
      <div className="captura-camara-visor">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{ display: fotoPreview ? 'none' : 'block' }}
        />
        {fotoPreview && <img src={fotoPreview} alt="Foto capturada" />}
      </div>
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      <div className="captura-camara-acciones">
        {!fotoPreview ? (
          <button type="button" className="btn-guardar" onClick={capturar} disabled={!listaParaCapturar}>
            <IconCamara size={16} /> Capturar
          </button>
        ) : (
          <>
            <button type="button" className="btn-cancelar" onClick={repetirToma} disabled={subiendo}>
              <IconRenovar size={16} stroke={STROKE_DEGRADE} /> Repetir toma
            </button>
            <button type="button" className="btn-guardar" onClick={() => setMostrarRecorte(true)} disabled={subiendo}>
              {subiendo ? 'Subiendo...' : <><IconExito size={16} /> Usar esta foto</>}
            </button>
          </>
        )}
      </div>

      {mostrarRecorte && (
        <RecorteFotoModal
          imagenSrc={fotoPreview}
          subiendo={subiendo}
          onConfirmar={alConfirmarRecorte}
          onCancelar={() => setMostrarRecorte(false)}
        />
      )}
    </div>
  );
};

export default CapturaFotoCamara;
